// src/components/digital_twin/twin_engine/ui/TelemetryTab.js
// Live sensors linked to the asset: latest value (page stream), state vs. thresholds, freshness,
// and the recent history from the backend.
import React, { memo } from 'react';
import { Badge, Box, Flex, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { FaSatelliteDish } from 'react-icons/fa';
import { Empty, LevelBadge, Section, TrendChart, ago, useTwinTokens } from './ui';

const STATE_LEVEL = { critical: 'critical', warning: 'watch', ok: 'ok' };

function TelemetryTab({ sensors, profile, historical }) {
  const t = useTwinTokens();
  const hist = historical?.data?.sensors || null;
  if (!sensors.length && !hist?.length) {
    return (
      <Empty icon={FaSatelliteDish} title="No sensor linked to this asset">
        This {profile.label.toLowerCase()} is shown from backend records only (register, ML health, inspections, terrain).
        Link a sensor in Asset Management to stream live readings here.
      </Empty>
    );
  }
  return (
    <VStack align="stretch" spacing={4}>
      {hist?.length > 0 && (
        <Section title="Readings at replay time" icon={FaSatelliteDish}>
          {hist.map((s) => (
            <Flex key={s.sensor_id} justify="space-between" py={1.5}>
              <Text fontSize="13px">{s.name || s.sensor_id} <Text as="span" color={t.muted}>({s.type})</Text></Text>
              <Text fontSize="13px" fontFamily="mono" fontWeight="700">{s.value ?? '—'}</Text>
            </Flex>
          ))}
        </Section>
      )}
      {sensors.map((s) => (
        <Section key={s.key} title={s.name} icon={FaSatelliteDish}
          right={<HStack spacing={1}>
            <Badge colorScheme={s.online === false ? 'red' : s.online ? 'green' : 'gray'} fontSize="10px">{s.online === false ? 'OFFLINE' : s.online ? 'ONLINE' : 'UNKNOWN'}</Badge>
            <LevelBadge level={STATE_LEVEL[s.state] || 'watch'}>{s.state}</LevelBadge>
          </HStack>}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={s.history ? 3 : 0}>
            <Box>
              <Text fontSize="11px" color={t.muted} fontWeight="700">LATEST</Text>
              <Text fontSize="2xl" fontWeight="900" fontFamily="mono">{s.value != null ? s.value : s.label || '—'}<Text as="span" fontSize="sm" color={t.muted} ml={1}>{s.value != null ? s.unit : ''}</Text></Text>
              <Text fontSize="11px" color={t.muted}>{s.at ? ago(s.at) : 'no reading yet'}</Text>
            </Box>
            <Box>
              <Text fontSize="11px" color={t.muted} fontWeight="700">LIMITS</Text>
              <Text fontSize="13px">{s.min != null ? `min ${s.min}` : 'no min'} · {s.max != null ? `max ${s.max}` : 'no max'}</Text>
              <Text fontSize="11px" color={t.muted}>{s.type}</Text>
            </Box>
            <Box>
              <Text fontSize="11px" color={t.muted} fontWeight="700">DEVICE</Text>
              <Text fontSize="13px" fontFamily="mono" noOfLines={1}>{s.key}</Text>
              <Text fontSize="11px" color={t.muted}>{s.history ? `${s.history.length} readings loaded` : 'no history returned'}</Text>
            </Box>
          </SimpleGrid>
          {s.history && (
            <TrendChart points={s.history} height={110} unit={s.unit ? ` ${s.unit}` : ''} label={`${s.name} history`}
              format={(v) => (Math.abs(v) < 10 ? v.toFixed(2) : v.toFixed(1))}
              bands={s.max != null ? [{ from: s.max, to: s.max * 10 + 1000, color: '#E53E3E' }] : []} />
          )}
        </Section>
      ))}
    </VStack>
  );
}

export default memo(TelemetryTab);
